<template>
  <div
    :class="['phoenix-keyboard-wrapper', `keyboard-${keyboardType}`]"
    id="phoenix-keyboard-wrapper"
  >
    <div :class="['StandardKeyboard', { outline: showOutline, disabled: keyboardLocked}]">
      <div v-for="row in displayKeys" :key="row.name" :class="[`row ${row.name}`, theme]">
        <component
          :is="getKwtKey(key)"
          v-for="key in row.keys"
          :key="key.name"
          :name="key.name"
          :character="key.character"
          :alt="key.alt"
          :shift="key.shift"
          :hold-hand="key.hold"
          :row="row.name"
          :class="{ tablet: ipadWithoutExternalKeyboard }"
        ></component>
      </div>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers, mapGetters } from 'vuex';
import BaseKeyboard from 'mixins/BaseKeyboard';
import { keyLayout } from '@/constants/standard-keyboard';
import StandardKey from './keys/StandardKey.vue';
import HoldKey from './keys/HoldKey.vue';

const keyboard = createNamespacedHelpers('keyboard');

const keys = {
  standard: StandardKey,
  hold: HoldKey
};

export default {
  components: {
    ...keys
  },

  mixins: [BaseKeyboard],

  computed: {
    ...keyboard.mapState(['capsLock']),
    ...mapGetters('application', ['ipadWithoutExternalKeyboard']),

    displayKeys() {
      if (!this.hideNumberRow) return keyLayout;
      return keyLayout.filter(row => row.name !== 'rowNumber');
    }
  },

  data() {
    return {
      processKeyPress: null
    };
  },

  created() {
    if (this.isTouchHold) {
      this.processKeyPress = this.processTouchHoldKey;
      return;
    }

    this.processKeyPress = this.processStandardKey;
  },

  methods: {
    ...keyboard.mapMutations(['setCapsLock']),

    addKeyListeners() {
      window.addEventListener('keydown', this.handleKeyDown);
      window.addEventListener('keyup', this.handleKeyUp);

      if (this.isTouchHold && !this.device.isMobile) {
        window.addEventListener('keydown', this.handleHoldKeyDown);
        window.addEventListener('keyup', this.handleHoldKeyUp);
      }
    },

    removeKeyListeners() {
      window.removeEventListener('keydown', this.handleKeyDown);
      window.removeEventListener('keydown', this.handleHoldKeyDown);

      window.removeEventListener('keyup', this.handleKeyUp);
      window.removeEventListener('keyup', this.handleHoldKeyUp);
    },

    getKwtKey(key) {
      if (this.isTouchHold && key.hold && this.targetKey === key.name) {
        return 'hold';
      }

      return 'standard';
    },

    processTouchHoldKey(key, { withShift }) {
      if (this.keyboardLocked) return;
      this.attemptKey({ key, withShift });
    },

    processStandardKey(key, { withShift }) {
      this.attemptKey({ key, withShift });
    },

    doNotHandleKey(event) {
      const { key } = event;

      return (
        key !== 'Backspace'
        && (this.keyboardDisabled
          || key === 'CapsLock'
          || this.keyNotUsedInActivity(key, event)
          || this.keyIsPressed({ event }))
      );
    },

    isShiftKeyTarget(event) {
      return this.targetKey.includes('shift') && this.isShiftKey(event.key);
    },

    handleHoldKeyDown(event) {
      event.preventDefault();
      
      if (this.keyNotUsedInActivity(event.key, event)) return;

      if (
        (event.key === this.targetKey || this.isShiftKeyTarget(event))
        && this.keyboardLocked
      ) {
        this.pressHoldKey();
        this.addToActiveKeys({ event });
      }
    },

    handleHoldKeyUp(event) {
      event.preventDefault();

      if (event.key === this.targetKey || this.isShiftKeyTarget(event)) {
        this.releaseHoldKey();
        this.removeFromActiveKeys({ event });
      }
    },

    handleKeyDown(event) {
      event.preventDefault();

      const { key, location = '' } = event;

      let isCapsLock = false;
      if (event.getModifierState && event.getModifierState('CapsLock')) {
        isCapsLock = true;
      }

      if (isCapsLock !== this.capsLock) {
        this.setCapsLock(isCapsLock);
      }

      if (this.doNotHandleKey(event)) return;

      const normalizedKey = this.normalizeKey(key, location);
      this.processKeyPress(normalizedKey, { withShift: event.shiftKey });

      // don't add shift to activeKey array. shift is a 'free' key
      if (!this.isShiftKey(key)) {
        this.addToActiveKeys({ event });
      }
    },

    handleKeyUp(event) {
      let isCapsLock = false;
      if (event.getModifierState && event.getModifierState('CapsLock')) {
        isCapsLock = true;
      }
      if (isCapsLock !== this.capsLock) {
        this.setCapsLock(isCapsLock);
      }
      const key = event.key || event;
      if (this.keyNotUsedInActivity(key, event)) return;

      this.removeFromActiveKeys({ event });

      if (this.keyboardLocked) return;

      this.resetAttemptedKey();
    },

    isShiftKey(keyPressed) {
      const key = keyPressed.toLowerCase();
      // iOS registers Shift as 'Dead'
      return key.includes('shift') || key === 'dead';
    },

    normalizeKey(key, location = '') {
      const normalized = {
        enter: 'enter',
        spacebar: ' ',
        quotationmark: '"',
        shift: `shift${location}`,
        tab: 'tab'
      };

      return normalized[key.toLowerCase()] || key;
    }
  }
};
</script>
